import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import useIsMobile from 'hooks/use-is-mobile';
import { useHasPermission } from 'hooks/use-has-permission';
import NavbarHeader from 'components/layout/navbar/navbar-header';
import NavbarProjectTree from 'components/layout/navbar/navbar-project-tree';
import NavbarMenuList, { type ExtendedMenuItem } from 'components/layout/navbar/navbar-menu-list';
import type { RailGroup } from 'components/layout/navbar/navbar-rail-groups';
import { BuildOutlined, CodeOutlined, DollarOutlined, ProfileOutlined } from '@ant-design/icons';
import { SettingOutlined, SnippetsOutlined, FileSearchOutlined, AlertOutlined } from '@ant-design/icons';
import { usePayGateFeature } from 'hooks/use-pay-gate-feature';
import { useFeatureFlag } from 'hooks/use-feature-flags';
import { FeatureFlag } from 'constants/feature-flags';

interface Props {
  projectId?: string;
}

const ProjectMenu = ({ projectId }: Props) => {
  const { isMobile } = useIsMobile();
  const { isAuthorized: hasPermissionToEditProjectSettings, isLoading: isLoadingPermissionToEditProjectSettings } =
    useHasPermission('EDIT_PROJECT_SETTINGS', { projectId });
  const { isAuthorized: hasPermissionToViewProject, isLoading: isLoadingPermissionToViewProject } = useHasPermission(
    'VIEW_PROJECT',
    { projectId }
  );
  const { isAuthorized: hasPermissionToViewDriftCauses, isLoading: isLoadingPermissionToViewDriftCauses } =
    useHasPermission('VIEW_DRIFT_CAUSE', { projectId });
  const { activated: isDriftActionsActivated, isLoading: isLoadingDriftActionsPayGate } =
    usePayGateFeature('driftActions');

  const isProjectTreeEnabled = useFeatureFlag(FeatureFlag.PROJECT_SIDEBAR_TREE);

  const isLoading =
    isLoadingPermissionToEditProjectSettings ||
    isLoadingPermissionToViewProject ||
    isLoadingPermissionToViewDriftCauses ||
    isLoadingDriftActionsPayGate;

  const menuItems = useMemo<ExtendedMenuItem[]>(() => {
    return [
      {
        key: 'project-menu-environments',
        title: <FormattedMessage id="navigation.project.menu.environments" />,
        to: `/p/${projectId}/environments`,
        icon: <BuildOutlined />,
        shouldShow: true,
        isLoading: isLoading
      },
      {
        key: 'project-menu-costs',
        title: <FormattedMessage id="navigation.project.menu.costs" />,
        to: `/p/${projectId}/costs`,
        icon: <DollarOutlined />,
        shouldShow: hasPermissionToEditProjectSettings,
        isLoading: isLoading
      },
      {
        key: 'project-menu-discovery',
        title: <FormattedMessage id="navigation.project.menu.discovery" />,
        to: `/p/${projectId}/discovery`,
        icon: <FileSearchOutlined />,
        shouldShow: !isMobile && hasPermissionToEditProjectSettings,
        isLoading: isLoading
      },
      {
        key: 'project-menu-templates',
        title: <FormattedMessage id="navigation.project.menu.templates" />,
        to: `/p/${projectId}/templates`,
        icon: <SnippetsOutlined />,
        shouldShow: hasPermissionToViewProject,
        isLoading: isLoading
      },
      {
        key: 'project-menu-variables',
        title: <FormattedMessage id="navigation.project.menu.variables" />,
        to: `/p/${projectId}/variables`,
        icon: <CodeOutlined />,
        shouldShow: !isMobile && hasPermissionToViewProject,
        isLoading: isLoading
      },
      {
        key: 'project-menu-settings',
        title: <FormattedMessage id="navigation.project.menu.settings" />,
        to: `/p/${projectId}/settings`,
        icon: <SettingOutlined />,
        shouldShow: hasPermissionToEditProjectSettings,
        isLoading: isLoading
      },
      {
        key: 'project-menu-actions-title',
        title: <FormattedMessage id="navigation.actions" />,
        isSectionTitle: true as const,
        shouldShow: hasPermissionToViewDriftCauses && isDriftActionsActivated,
        isLoading: isLoading
      },
      {
        key: 'project-menu-drifts',
        title: <FormattedMessage id="navigation.project.menu.drifts" />,
        to: `/p/${projectId}/actions`,
        icon: <AlertOutlined />,
        shouldShow: hasPermissionToViewDriftCauses && isDriftActionsActivated,
        isLoading: isLoading
      }
    ];
  }, [
    projectId,
    isMobile,
    hasPermissionToEditProjectSettings,
    hasPermissionToViewProject,
    hasPermissionToViewDriftCauses,
    isDriftActionsActivated,
    isLoading
  ]);

  return (
    <>
      {isProjectTreeEnabled && projectId && <NavbarProjectTree projectId={projectId} />}
      <NavbarHeader menuTitle="Project Menu" menuIcon={isProjectTreeEnabled ? undefined : <ProfileOutlined />} />
      <NavbarMenuList items={menuItems} groups={RAIL_GROUPS} />
    </>
  );
};

const RAIL_GROUPS: RailGroup[] = [
  { keys: ['project-menu-environments'] },
  {
    titleId: 'navigation.section.configuration',
    keys: ['project-menu-costs', 'project-menu-discovery', 'project-menu-templates', 'project-menu-variables']
  },
  { keys: ['project-menu-actions-title', 'project-menu-drifts'] },
  { titleId: 'navigation.section.settings', keys: ['project-menu-settings'] }
];

export default ProjectMenu;
